import { Alert, AlertDescription, Button, Input, Label } from '@sitewise/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';


import { adminTotpConfirm } from '../../../api/auth.js';
import { ApiError } from '../../../lib/api-client.js';
import { AuthShell } from '../../auth/auth-shell.js';

interface PendingTotpSetup {
  email: string;
  password: string;
  totpSecret: string;
  otpauthUrl: string;
  qrDataUrl: string;
}

const STORAGE_KEY = 'sitewise:totp-setup';

export function AdminTotpSetupPage() {
  const navigate = useNavigate();
  const [pending, setPending] = useState<PendingTotpSetup | null>(null);
  const [code, setCode] = useState('');

  useEffect(() => {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      navigate('/admin/login', { replace: true });
      return;
    }
    try {
      setPending(JSON.parse(raw) as PendingTotpSetup);
    } catch {
      sessionStorage.removeItem(STORAGE_KEY);
      navigate('/admin/login', { replace: true });
    }
  }, [navigate]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!pending) throw new Error('Setup not in progress');
      return adminTotpConfirm({
        email: pending.email,
        password: pending.password,
        totpCode: code,
        totpSecret: pending.totpSecret,
      });
    },
    onSuccess: () => {
      sessionStorage.removeItem(STORAGE_KEY);
      navigate('/admin/dashboard', { replace: true });
    },
  });

  if (!pending) return null;

  return (
    <AuthShell
      title="Set up TOTP"
      description="Scan this QR with your authenticator (1Password, Authy, Google Authenticator), then enter the 6-digit code below."
    >
      <div className="flex flex-col items-center gap-3">
        <img
          src={pending.qrDataUrl}
          alt="TOTP QR code"
          className="h-40 w-40 rounded-md border border-border bg-white p-2"
        />
        <details className="w-full text-xs text-fg-secondary">
          <summary className="cursor-pointer text-fg-muted">Can't scan? Show secret</summary>
          <code className="mt-1 block break-all rounded bg-surface-muted p-2 text-fg">
            {pending.totpSecret}
          </code>
        </details>
      </div>

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
        noValidate
      >
        <div className="space-y-1.5">
          <Label htmlFor="totp-code">6-digit code</Label>
          <Input
            id="totp-code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            autoComplete="one-time-code"
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D+/g, ''))}
            autoFocus
          />
        </div>
        {mutation.error ? (
          <Alert variant="danger">
            <AlertDescription>
              {mutation.error instanceof ApiError
                ? mutation.error.message
                : 'Could not enable TOTP.'}
            </AlertDescription>
          </Alert>
        ) : null}
        <Button type="submit" className="w-full" disabled={mutation.isPending}>
          {mutation.isPending ? 'Verifying…' : 'Enable TOTP and continue'}
        </Button>
      </form>
    </AuthShell>
  );
}
